import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { ILocationContext } from '../../../model/location-context';
import {
    IMaintenanceCommandContext,
    IMaintenanceComponent,
    IMaintenanceContext,
    IMaintenanceFailedTest,
    IMaintenanceLink,
    IMaintenanceModeContext,
    IMaintenanceScheduleItem,
    IMaintenanceTaskCompleted
} from '../../../model/maintenance';
import { LogService } from '../logging/log.service';
import { ICommand, ICommandContext } from '../../../model/docking';
import MaintenanceActions from './store/maintenance.action-types';
import { MaintenanceState } from './store/maintenance.reducers';
import {
    maintenanceCommandsSelector,
    maintenanceComponentsSelector,
    maintenanceContextSelector,
    maintenanceFailedTestsSelector,
    maintenanceLinksSelector,
    maintenanceModeContextSelector,
    maintenanceScheduleSelector,
    maintenanceTasksCompletedSelector
} from './store/maintenance.selectors';
import { IUserPinStore } from '../../../model/maintenance.interface';

@Injectable({
    providedIn: 'root'
})
export class MaintenanceService implements IUserPinStore {
    public userEnteredPin = '';

    LOG_SOURCE = 'MaintenanceService';

    constructor(private readonly store: Store<MaintenanceState>, private readonly logService: LogService) {}

    // Device
    setDeviceIdInSession(deviceId: string) {
        sessionStorage.setItem('deviceId', deviceId);
    }

    getDeviceIdFromSession() {
        return sessionStorage.getItem('deviceId');
    }

    // Maintenance Context
    requestMaintenanceContext(locationContext: ILocationContext, deviceId: string) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceContext({ locationContext, deviceId }));
    }

    loadMaintenanceContext(maintenanceContext: IMaintenanceContext) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceContext({ maintenanceContext }));
    }

    getMaintenanceContext(): Observable<IMaintenanceContext> {
        return this.store.select(maintenanceContextSelector);
    }

    triggerTestCommand(locationContext: ILocationContext, command: ICommand, deviceId: string, operator: string) {
        const commandContext: ICommandContext = {
            locationContext,
            command
        };
        this.store.dispatch(
            MaintenanceActions.triggerTestCommand({
                commandContext: commandContext,
                deviceId: deviceId,
                operator: operator
            })
        );
        this.logService.info(this.LOG_SOURCE, `Executed test command '${command.text}'`, { command });
    }

    triggerTestBackCommand(locationContext: ILocationContext, deviceId: string) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceContext({ locationContext, deviceId }));
    }

    // Maintenance Mode
    setMaintenanceMode(locationContext: ILocationContext, inMaintenanceMode: boolean) {
        const maintenanceModeContext: IMaintenanceModeContext = {
            locationContext,
            inMaintenanceMode
        };
        this.store.dispatch(MaintenanceActions.setMaintenanceMode({ maintenanceModeContext }));

        if (inMaintenanceMode) {
            this.logService.info(this.LOG_SOURCE, `Activated maintenance mode`, { inMaintenanceMode });
        } else {
            this.logService.info(this.LOG_SOURCE, `Deactivated Maintenance mode`, { inMaintenanceMode });
        }
    }

    requestMaintenanceModeContext(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceModeContext({ locationContext }));
    }

    loadMaintenanceModeContext(maintenanceModeContext: IMaintenanceModeContext) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceModeContext({ maintenanceModeContext }));
    }

    getMaintenanceModeContext(): Observable<IMaintenanceModeContext> {
        return this.store.select(maintenanceModeContextSelector);
    }

    // Links
    requestMaintenanceLinks(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceLinks({ locationContext }));
    }

    loadMaintenanceLinks(maintenanceLinks: IMaintenanceLink[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceLinks({ maintenanceLinks }));
    }

    getMaintenanceLinks(): Observable<IMaintenanceLink[]> {
        return this.store.select(maintenanceLinksSelector);
    }

    // Commands
    requestMaintenanceCommands(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceCommands({ locationContext }));
    }

    loadMaintenanceCommands(maintenanceCommands: string[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceCommands({ maintenanceCommands }));
    }

    getMaintenanceCommands(): Observable<string[]> {
        return this.store.select(maintenanceCommandsSelector);
    }

    triggerMaintenanceCommand(locationContext: ILocationContext, command: string) {
        const maintenanceCommandContext: IMaintenanceCommandContext = {
            locationContext,
            command
        };
        this.store.dispatch(MaintenanceActions.triggerMaintenanceCommand({ maintenanceCommandContext }));

        this.logService.info(this.LOG_SOURCE, `Executed maintenance command '${command}'`, { command });
    }

    // Failed Tests
    requestMaintenanceFailedTests(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceFailedTests({ locationContext }));
    }

    loadMaintenanceFailedTests(maintenanceFailedTests: IMaintenanceFailedTest[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceFailedTests({ maintenanceFailedTests }));
    }

    getMaintenanceFailedTests(): Observable<IMaintenanceFailedTest[]> {
        return this.store.select(maintenanceFailedTestsSelector);
    }

    // Components
    requestMaintenanceComponents(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceComponents({ locationContext }));
    }

    loadMaintenanceComponents(maintenanceComponents: IMaintenanceComponent[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceComponents({ maintenanceComponents }));
    }

    getMaintenanceComponents(): Observable<IMaintenanceComponent[]> {
        return this.store.select(maintenanceComponentsSelector);
    }

    // Schedule
    requestMaintenanceSchedule(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceSchedule({ locationContext }));
    }

    loadMaintenanceSchedule(maintenanceSchedule: IMaintenanceScheduleItem[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceSchedule({ maintenanceSchedule }));
    }

    getMaintenanceSchedule(): Observable<IMaintenanceScheduleItem[]> {
        return this.store.select(maintenanceScheduleSelector);
    }

    // Tasks Completed
    requestMaintenanceTasksCompleted(locationContext: ILocationContext) {
        this.store.dispatch(MaintenanceActions.requestMaintenanceTasksCompleted({ locationContext }));
    }

    loadMaintenanceTasksCompleted(maintenanceTasksCompleted: IMaintenanceTaskCompleted[]) {
        this.store.dispatch(MaintenanceActions.loadMaintenanceTasksCompleted({ maintenanceTasksCompleted }));
    }

    getMaintenanceTasksCompleted(): Observable<IMaintenanceTaskCompleted[]> {
        return this.store.select(maintenanceTasksCompletedSelector);
    }

    addMaintenanceTaskCompleted(
        locationContext: ILocationContext,
        maintenanceTaskCompleted: IMaintenanceTaskCompleted
    ) {
        this.store.dispatch(
            MaintenanceActions.addMaintenanceTaskCompleted({ locationContext, maintenanceTaskCompleted })
        );

        this.logService.info(this.LOG_SOURCE, `Added maintenance task`, { maintenanceTaskCompleted });
    }

    storeUserPin(pin: string): void {
        this.userEnteredPin = pin;
    }

    clearUserPin(): void {
        this.userEnteredPin = '';
    }
}
